'use client';

import type { ReactNode } from 'react';
import {
  isMcpToolName,
  normalizeToolName,
} from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import { AdEntitiesListBody } from './ad-entities-list-body';
import { AdEntityDetailBody } from './ad-entity-detail-body';
import { AdGraphListBody } from './ad-graph-list-body';
import { AdRelationshipsListBody } from './ad-relationships-list-body';
import { DiscoverySignalOutputBody } from './discovery-signal-card-renderer';
import { McpBloodhoundOutputBody } from './mcp-bloodhound-output-renderer';
import { parseAdEntitiesListOutput } from './parse-ad-entities-output.util';
import { parseAdEntityDetailsOutput } from './parse-ad-entity-details-output.util';
import { parseAdGraphOutput } from './parse-ad-graph-output.util';
import { parseAdRelationshipsOutput } from './parse-ad-relationships-output.util';
import { INTERNAL_NETWORK_TOOL_NAMES } from './internal-network-tool-names';

function getOutputText(output: unknown): string {
  if (typeof output === 'string') {
    return output;
  }
  if (output !== undefined && output !== null) {
    return JSON.stringify(output);
  }
  return '';
}

export function renderInternalNetworkToolOutputBody({
  toolName,
  output,
}: {
  toolName: string;
  output: unknown;
}): ReactNode {
  const normalized = normalizeToolName(toolName);
  const text = getOutputText(output);

  if (!text.trim()) {
    return null;
  }

  switch (normalized) {
    case normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.LIST_AD_ENTITIES): {
      const parsed = parseAdEntitiesListOutput(text);
      if (parsed && parsed.entities.length > 0) {
        return <AdEntitiesListBody entities={parsed.entities} />;
      }
      break;
    }
    case normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.GET_AD_ENTITY_DETAILS): {
      const parsed = parseAdEntityDetailsOutput(text);
      if (parsed) {
        return <AdEntityDetailBody entity={parsed} />;
      }
      break;
    }
    case normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.QUERY_AD_GRAPH): {
      const parsed = parseAdGraphOutput(text);
      if (parsed) {
        return <AdGraphListBody graph={parsed} />;
      }
      break;
    }
    case normalizeToolName(
      INTERNAL_NETWORK_TOOL_NAMES.GET_AD_ENTITY_RELATIONSHIPS,
    ): {
      const parsed = parseAdRelationshipsOutput(text);
      if (parsed && parsed.relationships.length > 0) {
        return <AdRelationshipsListBody relationships={parsed.relationships} />;
      }
      break;
    }
    case normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.CREATE_DISCOVERY_SIGNAL):
    case normalizeToolName(
      INTERNAL_NETWORK_TOOL_NAMES.GET_EXISTING_DISCOVERY_SIGNALS,
    ):
    case normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.CREATE_SCENARIO_DRAFT):
      return <DiscoverySignalOutputBody output={text} />;
    default:
      if (isMcpToolName(toolName)) {
        return <McpBloodhoundOutputBody output={text} />;
      }
  }

  return null;
}

export function getInternalNetworkToolResultCount({
  toolName,
  output,
}: {
  toolName: string;
  output: unknown;
}): number | undefined {
  const normalized = normalizeToolName(toolName);
  const text = getOutputText(output);

  if (
    normalized ===
    normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.LIST_AD_ENTITIES)
  ) {
    const parsed = parseAdEntitiesListOutput(text);
    return parsed?.entities.length;
  }

  if (
    normalized ===
    normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.GET_AD_ENTITY_RELATIONSHIPS)
  ) {
    const parsed = parseAdRelationshipsOutput(text);
    return parsed?.relationships.length;
  }

  if (
    normalized === normalizeToolName(INTERNAL_NETWORK_TOOL_NAMES.QUERY_AD_GRAPH)
  ) {
    const parsed = parseAdGraphOutput(text);
    return parsed?.nodeCount;
  }

  return undefined;
}
